/**
 * StageManagementPanel — SuperAdmin Sistem → Tapşırıq Etapları.
 * - List with drag-reorder (@hello-pangea/dnd)
 * - Edit dialog (name, color, flags)
 * - Add new stage
 * - Delete with "move tasks to..." prompt
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, GripVertical, Edit2, Trash2, Save, X, Flag, CheckSquare, ShieldCheck, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = [
  { key: 'zinc', label: 'Boz', bg: 'bg-zinc-100 border-zinc-300', dot: 'bg-zinc-500' },
  { key: 'blue', label: 'Mavi', bg: 'bg-blue-100 border-blue-300', dot: 'bg-blue-500' },
  { key: 'amber', label: 'Sarı', bg: 'bg-amber-100 border-amber-300', dot: 'bg-amber-500' },
  { key: 'red', label: 'Qırmızı', bg: 'bg-red-100 border-red-300', dot: 'bg-red-500' },
  { key: 'emerald', label: 'Yaşıl', bg: 'bg-emerald-100 border-emerald-300', dot: 'bg-emerald-500' },
  { key: 'purple', label: 'Bənövşəyi', bg: 'bg-purple-100 border-purple-300', dot: 'bg-purple-500' },
  { key: 'pink', label: 'Çəhrayı', bg: 'bg-pink-100 border-pink-300', dot: 'bg-pink-500' },
  { key: 'orange', label: 'Narıncı', bg: 'bg-orange-100 border-orange-300', dot: 'bg-orange-500' },
  { key: 'cyan', label: 'Cyan', bg: 'bg-cyan-100 border-cyan-300', dot: 'bg-cyan-500' },
];

function colorOf(key) {
  return COLORS.find((c) => c.key === key) || COLORS[0];
}

export default function StageManagementPanel() {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | {...stage} | {__new: true}
  const [deletePrompt, setDeletePrompt] = useState(null);
  const [moveTarget, setMoveTarget] = useState('');

  const fetchStages = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/task-stages`, { headers });
      setStages(r.data || []);
    } catch (e) {
      toast.error('Etapları yükləmək alınmadı');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchStages();
  }, [fetchStages]);

  const handleReorder = async (result) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;
    const reordered = [...stages];
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);
    setStages(reordered);
    try {
      await axios.post(
        `${API}/task-stages/reorder`,
        { stage_ids: reordered.map((s) => s.id) },
        { headers }
      );
      toast.success('Sıralama yadda saxlanıldı');
    } catch (e) {
      toast.error('Sıralama uğursuz');
      fetchStages();
    }
  };

  const handleSave = async (form) => {
    try {
      if (form.id) {
        await axios.put(`${API}/task-stages/${form.id}`, form, { headers });
        toast.success('Yeniləndi');
      } else {
        await axios.post(`${API}/task-stages`, form, { headers });
        toast.success('Etap əlavə edildi');
      }
      setEditing(null);
      fetchStages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Saxlanılmadı');
    }
  };

  const handleDelete = async () => {
    if (!deletePrompt) return;
    try {
      const params = moveTarget ? `?move_tasks_to=${moveTarget}` : '';
      await axios.delete(`${API}/task-stages/${deletePrompt.id}${params}`, { headers });
      toast.success('Silindi');
      setDeletePrompt(null);
      setMoveTarget('');
      fetchStages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinmədi');
    }
  };

  return (
    <div className="space-y-4" data-testid="stage-management-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Flag className="h-5 w-5 text-purple-600" />
            Tapşırıq Etapları
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Kanban sütunlarının (etapların) idarəedilməsi — sıra, ad, rəng, davranış
          </p>
        </div>
        <Button
          onClick={() => setEditing({ __new: true, name: '', color: 'zinc', is_initial: false, is_done: false, is_approval_gate: false })}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="add-stage-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Yeni etap
        </Button>
      </div>

      {loading && <div className="text-sm text-zinc-500">Yüklənir...</div>}

      <DragDropContext onDragEnd={handleReorder}>
        <Droppable droppableId="stages">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="space-y-2"
              data-testid="stages-list"
            >
              {stages.map((s, idx) => {
                const c = colorOf(s.color);
                return (
                  <Draggable key={s.id} draggableId={s.id} index={idx}>
                    {(prov, snap) => (
                      <div
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 ${c.bg} bg-white ${
                          snap.isDragging ? 'shadow-xl' : 'shadow-sm'
                        }`}
                        data-testid={`stage-row-${s.key}`}
                      >
                        <div {...prov.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                          <GripVertical className="h-4 w-4 text-zinc-400" />
                        </div>
                        <div className="text-xs font-mono text-zinc-400 w-6">#{idx + 1}</div>
                        <div className={`h-3 w-3 rounded-full ${c.dot}`} />
                        <div className="flex-1">
                          <div className="font-semibold text-sm">{s.name}</div>
                          <div className="text-[10px] text-zinc-500 font-mono">{s.key}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          {s.is_initial && (
                            <Badge className="bg-blue-100 text-blue-700 text-[10px]" data-testid={`stage-initial-${s.key}`}>
                              İlkin
                            </Badge>
                          )}
                          {s.approval_level === 1 && (
                            <Badge className="bg-amber-100 text-amber-700 text-[10px]" data-testid={`stage-approval-l1-${s.key}`}>
                              <ShieldCheck className="h-2.5 w-2.5 mr-0.5" /> Mərhələ 1
                            </Badge>
                          )}
                          {s.approval_level === 2 && (
                            <Badge className="bg-purple-100 text-purple-700 text-[10px]" data-testid={`stage-approval-l2-${s.key}`}>
                              <ShieldCheck className="h-2.5 w-2.5 mr-0.5" /> Mərhələ 2
                            </Badge>
                          )}
                          {s.is_archived && (
                            <Badge className="bg-zinc-200 text-zinc-700 text-[10px]" data-testid={`stage-archived-${s.key}`}>
                              <Archive className="h-2.5 w-2.5 mr-0.5" /> Arxiv
                            </Badge>
                          )}
                          {s.is_done && !s.is_archived && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid={`stage-done-${s.key}`}>
                              <CheckSquare className="h-2.5 w-2.5 mr-0.5" /> Final
                            </Badge>
                          )}
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => setEditing({ ...s })}
                          data-testid={`stage-edit-${s.key}`}
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-red-600 hover:bg-red-50"
                          onClick={() => setDeletePrompt(s)}
                          data-testid={`stage-delete-${s.key}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Edit/Create Dialog */}
      {editing && (
        <StageEditDialog
          stage={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deletePrompt} onOpenChange={(v) => !v && setDeletePrompt(null)}>
        <DialogContent data-testid="stage-delete-dialog">
          <DialogHeader>
            <DialogTitle>«{deletePrompt?.name}» silinsin?</DialogTitle>
            <DialogDescription>
              Bu etapdakı tapşırıqlar başqa bir etaba köçürülməlidir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Tapşırıqları köçür:</label>
            <Select value={moveTarget} onValueChange={setMoveTarget}>
              <SelectTrigger data-testid="stage-delete-target">
                <SelectValue placeholder="Hədəf etap seçin..." />
              </SelectTrigger>
              <SelectContent>
                {stages
                  .filter((x) => x.id !== deletePrompt?.id)
                  .map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      {s.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePrompt(null)}>Ləğv et</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              data-testid="stage-delete-confirm"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== Edit Dialog ====================
function StageEditDialog({ stage, onClose, onSave }) {
  const [form, setForm] = useState({
    id: stage.__new ? null : stage.id,
    name: stage.name || '',
    color: stage.color || 'zinc',
    is_initial: !!stage.is_initial,
    is_done: !!stage.is_done,
    is_approval_gate: !!stage.is_approval_gate,
    approval_level: stage.approval_level || null,
    is_archived: !!stage.is_archived,
  });

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast.error('Ad boş ola bilməz');
      return;
    }
    onSave(form);
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="stage-edit-dialog">
        <DialogHeader>
          <DialogTitle>{stage.__new ? 'Yeni etap' : 'Etap redaktəsi'}</DialogTitle>
          <DialogDescription className="sr-only">
            Kanban etabının ad, rəng və davranış parametrləri.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Ad *</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="məs. Yoxlanılır"
              data-testid="stage-name-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Rəng</label>
            <div className="grid grid-cols-9 gap-2">
              {COLORS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setForm({ ...form, color: c.key })}
                  className={`h-8 w-8 rounded-full ${c.dot} ${
                    form.color === c.key ? 'ring-2 ring-offset-2 ring-purple-500' : ''
                  }`}
                  title={c.label}
                  data-testid={`stage-color-${c.key}`}
                />
              ))}
            </div>
          </div>
          <div className="space-y-2 border-t pt-3">
            <label className="text-sm font-medium block">Davranış</label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_initial}
                onChange={(e) => setForm({ ...form, is_initial: e.target.checked, is_done: e.target.checked ? false : form.is_done })}
                data-testid="stage-flag-initial"
              />
              <span className="text-sm">İlkin etap (yeni tapşırıq buradan başlayır)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_done}
                onChange={(e) => setForm({ ...form, is_done: e.target.checked, is_initial: e.target.checked ? false : form.is_initial })}
                data-testid="stage-flag-done"
              />
              <span className="text-sm">Final etap (tamamlanmış sayılır)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_archived}
                onChange={(e) => setForm({ ...form, is_archived: e.target.checked, is_done: e.target.checked ? true : form.is_done })}
                data-testid="stage-flag-archived"
              />
              <span className="text-sm">Arxiv (default-da gizlədilir)</span>
            </label>
            <div className="pt-2 border-t">
              <label className="text-sm font-medium block mb-1">Təsdiq mərhələsi</label>
              <Select
                value={String(form.approval_level || 'none')}
                onValueChange={(v) => {
                  const lv = v === 'none' ? null : parseInt(v);
                  setForm({ ...form, approval_level: lv, is_approval_gate: !!lv });
                }}
              >
                <SelectTrigger data-testid="stage-approval-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Təsdiq tələb etmir —</SelectItem>
                  <SelectItem value="1">Mərhələ 1 — Qrup admini təsdiqi</SelectItem>
                  <SelectItem value="2">Mərhələ 2 — SuperAdmin təsdiqi və arxiv</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-1" /> Ləğv et
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="stage-save-btn"
          >
            <Save className="h-4 w-4 mr-1" /> Yadda saxla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
